import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    Button,
    Radio,
    Progress,
    Typography,
    Space,
    Row,
    Col,
    Statistic,
    Result,
    Modal,
    message,
    Spin,
    Alert,
    Tag,
    Steps,
    Image,
} from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    FilePdfOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;
const { Countdown } = Statistic;

const StudentExam = () => {
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth, "Content-Type": "application/json" } };
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [examStarted, setExamStarted] = useState(false);
    const [examFinished, setExamFinished] = useState(false);
    const [examResult, setExamResult] = useState(null);
    const [timeLimit, setTimeLimit] = useState(0);
    const [randomizeQuestions, setRandomizeQuestions] = useState(true);
    const [randomizeChoices, setRandomizeChoices] = useState(true);
    const [questionOrder, setQuestionOrder] = useState([]);
    const [choiceOrders, setChoiceOrders] = useState({});
    const [deadline, setDeadline] = useState(null);
    const timerRef = useRef(null);
    const [maxChoicesToShow, setMaxChoicesToShow] = useState(4);
    const [choiceSelections, setChoiceSelections] = useState({});
    const [examAttemptId, setExamAttemptId] = useState(null);
    const [maxQuestionsToShow, setMaxQuestionsToShow] = useState(null);

    useEffect(() => {
        fetchCourseAndQuestions();
    }, [courseId]);

    const fetchCourseAndQuestions = async () => {
        try {
            setLoading(true);
            // Fetch course details
            const courseResponse = await axios.get(`/api/courses/${courseId}`, config);
            setCourse(courseResponse.data);
            setTimeLimit(courseResponse.data.exam_time_limit || 30);

            // Set max questions to show from course settings
            setMaxQuestionsToShow(courseResponse.data.exam_question_count);

            // Set max choices to show from course settings (ensure it's a number)
            const choiceCount = courseResponse.data.exam_choice_count;
            setMaxChoicesToShow(choiceCount && choiceCount > 0 ? choiceCount : null);

            // Fetch questions
            const questionsResponse = await axios.get(`/api/courses/${courseId}/questions`, config);
            setOriginalQuestions(questionsResponse.data);

            setLoading(false);
        } catch (error) {
            message.error("Failed to load exam");
            setLoading(false);
        }
    };

    // Function to shuffle array (Fisher-Yates algorithm)
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Select random questions if exam_question_count is set
    const selectQuestionsToShow = () => {
        if (!originalQuestions.length) return [];

        if (maxQuestionsToShow && maxQuestionsToShow < originalQuestions.length) {
            // Randomly select questions
            const shuffledQuestions = shuffleArray([...originalQuestions]);
            return shuffledQuestions.slice(0, maxQuestionsToShow);
        }

        // Return all questions if no limit or limit is greater than available questions
        return [...originalQuestions];
    };

    // Randomize question order
    const randomizeQuestionOrder = () => {
        // First, select which questions to show
        const selectedQuestions = selectQuestionsToShow();

        if (!selectedQuestions.length) return;

        if (randomizeQuestions) {
            // Shuffle the selected questions
            const shuffledQuestions = shuffleArray(selectedQuestions);
            setQuestions(shuffledQuestions);
        } else {
            // Keep original order of selected questions
            setQuestions(selectedQuestions);
        }

        // Initialize answers for selected questions only
        const initialAnswers = {};
        selectedQuestions.forEach((question) => {
            initialAnswers[question.id] = null;
        });
        setAnswers(initialAnswers);
    };

    // Select which choices to show for each question
    const selectChoicesToShow = () => {
        if (!originalQuestions.length) return {};

        const newChoiceSelections = {};

        originalQuestions.forEach((question) => {
            const correctChoiceIndex = question.choices.findIndex((choice) => choice.is_correct);
            const incorrectChoiceIndexes = question.choices
                .map((choice, index) => ({ index, isCorrect: choice.is_correct }))
                .filter((choice) => !choice.isCorrect)
                .map((choice) => choice.index);

            // Always include the correct choice
            const selectedIndexes = [correctChoiceIndex];

            // Determine how many choices to show for this question
            const choicesToShow = maxChoicesToShow || question.choices.length;

            // If we have fewer than choicesToShow choices total, use all of them
            if (question.choices.length <= choicesToShow) {
                newChoiceSelections[question.id] = Array.from({ length: question.choices.length }, (_, i) => i);
            } else {
                // Otherwise, randomly select incorrect choices to reach choicesToShow
                const shuffledIncorrect = shuffleArray(incorrectChoiceIndexes);
                const incorrectToShow = shuffledIncorrect.slice(0, choicesToShow - 1);

                // Combine correct and selected incorrect choices
                const combinedIndexes = [...selectedIndexes, ...incorrectToShow];
                newChoiceSelections[question.id] = combinedIndexes;
            }
        });

        return newChoiceSelections;
    };

    // Randomize choice order for each question
    const randomizeChoiceOrder = (choiceSelections) => {
        if (!originalQuestions.length || !choiceSelections) return {};

        const newChoiceOrders = {};

        originalQuestions.forEach((question) => {
            // Get the selected choices to show
            const selectedChoices =
                choiceSelections[question.id] || Array.from({ length: question.choices.length }, (_, i) => i);

            if (randomizeChoices) {
                // Create a shuffled order for this question's choices
                newChoiceOrders[question.id] = shuffleArray([...selectedChoices]);
            } else {
                // Keep original order but still only show the selected choices
                newChoiceOrders[question.id] = [...selectedChoices].sort((a, b) => a - b);
            }
        });

        return newChoiceOrders;
    };

    const startExam = async () => {
        try {
            const startExamResponse = await axios.post(
                `/api/exam/start-exam`,
                { courseId },
                { headers: { "auth-token": auth, "Content-Type": "application/json" } }
            );
            setExamAttemptId(startExamResponse.data.examAttemptId);

            // Step 1: Select which choices to show for each question
            const selectedChoices = selectChoicesToShow();
            setChoiceSelections(selectedChoices);

            // Step 2: Randomize choice order based on selected choices
            const orderedChoices = randomizeChoiceOrder(selectedChoices);
            setChoiceOrders(orderedChoices);

            // Step 3: Select and randomize questions
            randomizeQuestionOrder();

            // Step 4: Set deadline and start exam
            const deadline = Date.now() + timeLimit * 60 * 1000;
            setDeadline(deadline);
            setExamStarted(true);
        } catch (error) {
            message.error("Failed to start exam");
            console.error("Start exam error:", error);
        }
    };

    // Function to map randomized choice index back to original index
    const getOriginalChoiceIndex = (questionId, randomizedIndex) => {
        if (!choiceOrders[questionId] || randomizedIndex === null) return null;
        return choiceOrders[questionId][randomizedIndex];
    };

    // Function to display choices in randomized order
    const getRandomizedChoices = (question) => {
        if (!question || !choiceOrders[question.id]) {
            // Fallback: if no choice order is set, show all choices
            console.warn(`No choice order found for question ${question.id}, showing all choices`);
            return question.choices.map((choice, index) => ({
                ...choice,
                originalIndex: index,
            }));
        }

        const selectedChoiceIndexes = choiceOrders[question.id];
        return selectedChoiceIndexes.map((originalIndex) => ({
            ...question.choices[originalIndex],
            originalIndex, // Store the original index for later mapping
        }));
    };

    const handleAnswer = (questionId, randomizedChoiceIndex) => {
        setAnswers({
            ...answers,
            [questionId]: randomizedChoiceIndex,
        });
    };

    const goToNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const finishExam = () => {
        Modal.confirm({
            title: "ส่งข้อสอบ",
            content: "คุณแน่ใจหรือไม่ว่าต้องการส่งข้อสอบ? คุณจะไม่สามารถแก้ไขคำตอบได้หลังจากส่งแล้ว",
            onOk: submitExam,
            okText: "ยืนยัน",
            cancelText: "ยกเลิก",
        });
    };

    const timeExpired = () => {
        message.warning("Time is up! Submitting your exam...");
        submitExam();
    };

    const submitExam = async () => {
        // Calculate results
        let totalCorrect = 0;
        let totalPoints = 0;
        let requiredQuestionsCorrect = true;
        const questionResults = [];

        questions.forEach((question, questionIndex) => {
            const userRandomizedAnswerIndex = answers[question.id];
            // Convert the randomized choice index back to original index
            const userOriginalAnswerIndex =
                userRandomizedAnswerIndex !== null
                    ? getOriginalChoiceIndex(question.id, userRandomizedAnswerIndex)
                    : null;

            // Find the correct choice index in the original question
            const originalQuestion = originalQuestions.find((q) => q.id === question.id);
            const correctIndex = originalQuestion.choices.findIndex((choice) => choice.is_correct);

            const isCorrect = userOriginalAnswerIndex === correctIndex;

            if (isCorrect) {
                totalCorrect++;
                totalPoints += question.points;
            } else if (question.is_required) {
                requiredQuestionsCorrect = false;
            }

            // Get all the choices that were shown to the user
            const shownChoices = choiceOrders[question.id]
                ? choiceOrders[question.id].map((idx) => originalQuestion.choices[idx])
                : originalQuestion.choices;

            // Store the result with original ordering for review
            questionResults.push({
                questionId: question.id,
                questionText: question.question_text,
                explainAnswer: question.explain_answer,
                userAnswer:
                    userOriginalAnswerIndex !== null
                        ? originalQuestion.choices[userOriginalAnswerIndex].choice_text
                        : "ไม่ได้ตอบคำถาม",
                correctAnswer: originalQuestion.choices[correctIndex].choice_text,
                isCorrect,
                points: isCorrect ? question.points : 0,
                maxPoints: question.points,
                shownChoices: shownChoices.map((choice) => choice.choice_text),
            });
        });

        // Calculate total possible points
        const totalPossiblePoints = questions.reduce((sum, q) => sum + q.points, 0);

        // Calculate percentage
        const percentageScore = totalPossiblePoints > 0 ? (totalPoints / totalPossiblePoints) * 100 : 0;

        // Determine if passed based on threshold
        let passed = false;
        if (course.exam_pass_threshold_type === "percentage") {
            passed = percentageScore >= course.exam_pass_threshold;
        } else {
            // count
            passed = totalCorrect >= course.exam_pass_threshold;
        }

        // If any required question is wrong, fail the exam
        if (!requiredQuestionsCorrect) {
            passed = false;
        }

        try {
            if (courseId && examAttemptId) {
                await axios.post(
                    `/api/exam/submit-exam`,
                    { courseId, examAttemptId, passed },
                    { headers: { "auth-token": auth, "Content-Type": "application/json" } }
                );
            }
        } catch (error) {
            message.error("Failed to submit exam");
        }

        // Set exam result
        setExamResult({
            totalQuestions: questions.length,
            totalCorrect,
            totalPoints,
            totalPossiblePoints,
            percentageScore,
            passed,
            questionResults,
            requiredQuestionsCorrect,
            maxChoicesShown: maxChoicesToShow,
            totalQuestionsInBank: originalQuestions.length,
        });

        setExamFinished(true);
    };

    // ดาวน์โหลดใบประกาศนียบัตร
    const viewCertificates = () => {
        navigate("/certificates");
    };

    // ไปที่คำถามที่ระบุ
    const goToQuestion = (index) => {
        setCurrentQuestionIndex(index);
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                <Spin size="large" tip="Loading exam..." />
            </div>
        );
    }

    if (!course || originalQuestions.length === 0) {
        return (
            <Result
                status="warning"
                title="ไม่มีการสอบ"
                subTitle="บทเรียนนี้ยังไม่มีข้อสอบ"
                extra={
                    <Button type="primary" onClick={() => navigate("/course")}>
                        กลับไปยังหน้ารวมบทเรียน
                    </Button>
                }
            />
        );
    }

    if (examFinished && examResult) {
        return (
            <Card>
                <Result
                    status={examResult.passed ? "success" : "error"}
                    title={examResult.passed ? "สอบผ่าน!" : "สอบไม่ผ่าน"}
                    subTitle={
                        <div>
                            <Text>
                                คุณได้คะแนน {examResult.totalPoints} จาก {examResult.totalPossiblePoints} คะแนน (
                                {Math.round(examResult.percentageScore)}%)
                            </Text>
                            {!examResult.requiredQuestionsCorrect && (
                                <Alert message="คุณตอบคำถามที่จำเป็นผิด" type="error" style={{ marginTop: 16 }} />
                            )}
                        </div>
                    }
                    extra={[
                        examResult.passed && (
                            <Button
                                key="certificates"
                                type="primary"
                                icon={<FilePdfOutlined />}
                                onClick={viewCertificates}
                            >
                                ดูใบประกาศนียบัตร
                            </Button>
                        ),
                        !examResult.passed && (
                            <Button
                                type="primary"
                                key="review"
                                onClick={() =>
                                    navigate(`/course/${courseId}/exam-review`, {
                                        state: { examResult },
                                    })
                                }
                            >
                                เฉลยคำตอบ
                            </Button>
                        ),
                        <Button key="back" onClick={() => navigate(`/course/${courseId}`)}>
                            กลับไปยังบทเรียน
                        </Button>,
                    ]}
                >
                    <Card title="สรุปผลการสอบ">
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic
                                    title="คำตอบที่ถูกต้อง"
                                    value={`${examResult.totalCorrect}/${examResult.totalQuestions}`}
                                    valueStyle={{ color: examResult.passed ? "#3f8600" : "#cf1322" }}
                                    prefix={examResult.passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="คะแนนรวม"
                                    value={examResult.percentageScore.toFixed(1)}
                                    suffix="%"
                                    valueStyle={{ color: examResult.passed ? "#3f8600" : "#cf1322" }}
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="จำเป็นต้องผ่าน"
                                    value={
                                        course.exam_pass_threshold_type === "percentage"
                                            ? `${course.exam_pass_threshold}%`
                                            : `${course.exam_pass_threshold} จาก ${examResult.totalQuestions} ข้อ`
                                    }
                                />
                            </Col>
                            <Col span={6}>
                                <Statistic
                                    title="ข้อสอบที่ใช้"
                                    value={`${examResult.totalQuestions}/${examResult.totalQuestionsInBank}`}
                                    suffix="ข้อ"
                                />
                            </Col>
                        </Row>
                    </Card>
                </Result>
            </Card>
        );
    }

    if (!examStarted) {
        const questionsToShow = maxQuestionsToShow || originalQuestions.length;
        const choicesToShow = maxChoicesToShow || "ทั้งหมด";

        return (
            <Card>
                <Result
                    icon={<FileTextOutlined />}
                    title={`การสอบ: ${course.title}`}
                    subTitle={
                        <div>
                            <Text>
                                ข้อสอบจะแสดง {questionsToShow} ข้อ
                                {maxQuestionsToShow && maxQuestionsToShow < originalQuestions.length
                                    ? ` จากทั้งหมด ${originalQuestions.length} ข้อ (สุ่มเลือก)`
                                    : ""}
                            </Text>
                            <br />
                            <Text>
                                แต่ละข้อจะแสดงตัวเลือก {choicesToShow} ตัวเลือก
                                {maxChoicesToShow ? " (สุ่มเลือก)" : ""}
                            </Text>
                            <br />
                            <Text>คุณมีเวลาในการทำข้อสอบทั้งหมด {timeLimit} นาที</Text>
                        </div>
                    }
                    extra={
                        <Button type="primary" onClick={startExam}>
                            เริ่มสอบ
                        </Button>
                    }
                >
                    <Card title="กฎระเบียบการสอบ">
                        <Paragraph>
                            <ul>
                                <li>คุณต้องทำข้อสอบให้เสร็จภายใน {timeLimit} นาที</li>
                                <li>
                                    {course.exam_pass_threshold_type === "percentage"
                                        ? `คุณต้องได้คะแนนอย่างน้อย ${course.exam_pass_threshold}% ของคะแนนรวม`
                                        : `คุณต้องตอบคำถามถูกอย่างน้อย ${course.exam_pass_threshold} ข้อ`}{" "}
                                    จึงจะผ่าน
                                </li>
                                <li>
                                    ข้อสอบจะแสดง {questionsToShow} ข้อ
                                    {maxQuestionsToShow && maxQuestionsToShow < originalQuestions.length
                                        ? ` ที่สุ่มเลือกจากคลังข้อสอบ ${originalQuestions.length} ข้อ`
                                        : ""}
                                </li>
                                <li>
                                    แต่ละข้อจะแสดงตัวเลือก {choicesToShow} ตัวเลือก
                                    {maxChoicesToShow ? " ที่สุ่มเลือก (รวมคำตอบที่ถูกต้อง)" : ""}
                                </li>
                                <li>คุณสามารถสลับไปมาระหว่างคำถามโดยใช้ปุ่ม "ย้อนกลับ" และ "ถัดไป"</li>
                                <li>คำตอบของคุณจะถูกบันทึกโดยอัตโนมัติขณะที่คุณทำข้อสอบ</li>
                                <li>หากมีการรีโหลดหน้าเว็บไซต์ การทำข้อสอบจะถูกเริ่มใหม่</li>
                            </ul>
                        </Paragraph>
                    </Card>
                </Result>
            </Card>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    const selectedAnswer = answers[currentQuestion.id];
    const progress = Math.floor(((currentQuestionIndex + 1) / questions.length) * 100);
    const answeredCount = Object.values(answers).filter((answer) => answer !== null).length;

    // Get choices in randomized order
    const randomizedChoices = getRandomizedChoices(currentQuestion);

    return (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                <Col>
                    <Text strong>
                        คำถามที่ {currentQuestionIndex + 1} ของทั้งหมด {questions.length} ข้อ
                    </Text>
                </Col>
                <Col>
                    <Countdown
                        value={deadline}
                        format="mm:ss"
                        onFinish={timeExpired}
                        prefix={<ClockCircleOutlined />}
                    />
                </Col>
            </Row>

            <Progress percent={progress} status="active" />

            <Steps current={currentQuestionIndex} onChange={goToQuestion} size="small" style={{ marginBottom: "20px" }}>
                {questions.map((q, index) => {
                    let status = "wait";
                    if (index === currentQuestionIndex) {
                        status = "process";
                    } else if (answers[q.id] !== null) {
                        status = "finish";
                    }
                    return (
                        <Step
                            key={q.id}
                            title={`${index + 1}`}
                            status={status}
                            icon={answers[q.id] !== null ? <CheckCircleOutlined /> : undefined}
                        />
                    );
                })}
            </Steps>

            <Card
                title={
                    <div>
                        <Text>{currentQuestion.question_text}</Text>
                        {currentQuestion.is_required && (
                            <Tag color="red" style={{ marginLeft: 8 }}>
                                Required
                            </Tag>
                        )}
                    </div>
                }
                style={{ marginTop: 16 }}
            >
                {currentQuestion.image_path && (
                    <div
                        style={{
                            textAlign: "center",
                            marginBottom: 16,
                            padding: "8px",
                            border: "1px solid #f0f0f0",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa",
                        }}
                    >
                        <Image
                            src={currentQuestion.image_url || currentQuestion.image_path}
                            alt="Question Image"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "400px",
                                objectFit: "contain",
                            }}
                            preview={{
                                mask: (
                                    <div style={{ color: "white" }}>
                                        <EyeOutlined /> คลิกเพื่อดูรูปภาพขนาดใหญ่
                                    </div>
                                ),
                            }}
                        />
                    </div>
                )}
                <Radio.Group
                    onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                    value={selectedAnswer}
                    style={{ width: "100%" }}
                >
                    <Space direction="vertical" style={{ width: "100%" }}>
                        {randomizedChoices.map((choice, index) => (
                            <Radio key={index} value={index} style={{ display: "block", marginBottom: 8 }}>
                                {choice.choice_text}
                            </Radio>
                        ))}
                    </Space>
                </Radio.Group>
            </Card>

            <Row justify="space-between" style={{ marginTop: 20 }}>
                <Col>
                    <Button
                        type="default"
                        onClick={goToPreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                        icon={<ArrowLeftOutlined />}
                    >
                        ย้อนกลับ
                    </Button>
                </Col>
                <Col>
                    <Space>
                        <Text>
                            ตอบแล้ว {answeredCount} จาก {questions.length} คำถาม
                        </Text>
                        {currentQuestionIndex === questions.length - 1 ? (
                            <Button type="primary" onClick={finishExam} icon={<CheckOutlined />}>
                                ส่งข้อสอบ
                            </Button>
                        ) : (
                            <Button type="primary" onClick={goToNextQuestion} icon={<ArrowRightOutlined />}>
                                ถัดไป
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>
        </Card>
    );
};

export default StudentExam;
